import * as React from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  useWorkspaceLoaderData,
  type VisualizerInitData,
} from "@/shared/components/visualization/loader";
import { VizConfigBuilder } from "@/shared/config/VizConfig";
import {
  State,
  VizStateContext,
  VizStateDispatchContext,
  actions,
  reducer,
} from "@/shared/state/visualization";

export function VisualizationStateProvider({
  children,
}: React.PropsWithChildren) {
  const { fileId } = useParams();
  const location = useLocation();
  const workspaceResult = useWorkspaceLoaderData();

  const fileIds = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.getAll("file");
  }, [location.search]);

  const datasetId = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("dataset") ?? undefined;
  }, [location.search]);

  const [state, dispatch] = React.useReducer(
    reducer,
    initializeVizState(fileId, fileIds, datasetId, workspaceResult),
  );

  // When the file param from /files/:fileId changes, update the state.
  React.useEffect(
    function onFileChanged() {
      if (!fileId) {
        return;
      }

      dispatch(actions.putFiles([{ fileId }]));
    },
    [fileId],
  );

  return (
    <VizStateContext.Provider value={state}>
      <VizStateDispatchContext.Provider value={dispatch}>
        {children}
      </VizStateDispatchContext.Provider>
    </VizStateContext.Provider>
  );
}

function initializeVizState(
  fileId: string | undefined,
  fileIds: string[],
  datasetId: string | undefined,
  workspaceInitResult: VisualizerInitData,
): State {
  // If we have a workspace result from the loader, use that directly
  if (workspaceInitResult?.type === "workspace") {
    return workspaceInitResult.value;
  }

  const vizConfigBuilder = new VizConfigBuilder();

  // Seed datasources from either files or dataset in the URL
  if (datasetId) {
    // If dataset is provided, use that
    vizConfigBuilder.setDatasources([
      { type: "dataset", value: { datasetId } },
    ]);
  } else if (fileId) {
    // If files/:fileId is provided, use that
    vizConfigBuilder.addFile({ fileId });
    vizConfigBuilder.setDatasources([{ type: "file", value: { fileId } }]);
  } else if (fileIds.length > 0) {
    // Otherwise render the workspace with the files from url
    vizConfigBuilder.setFiles(fileIds.map((fileId) => ({ fileId })));
    vizConfigBuilder.setDatasources(
      fileIds.map((fileId) => ({ type: "file", value: { fileId } })),
    );
  }

  return vizConfigBuilder.build().toObject();
}
